import CustomBtn from 'components/fragments/CustomBtn';
import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import socketService from '../services/socketService';
import { createPortal } from 'react-dom';
import { posHandler } from '../redux/handlers';
import moment from 'moment';

const DEFAULT_CASH_DATA = {
  initial_cash: 0,
  total_cash_in: 0,
  total_cash_out: 0,
  total_sales: 0,
  fee_revenue: 0,
  total_amount: 0,
  system_balance: []
};

export default function CloseBalancePrint() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { branch } = useSelector(state => state.pos);
  const { businessSlug } = useParams();
  const rawPrintData = localStorage.getItem('closePrintObj');
  const [printData, setPrintData] = useState();
  const [posData, setPosData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [summaryData, setSummaryData] = useState(DEFAULT_CASH_DATA);
  const [businessInfo, setBusinessInfo] = useState({
    businessName: '',
    branchName: '',
    vatCode: '',
    taxNumber: '',
    vatCalculator: ''
  });

  const handleEndSession = () => {
    localStorage.removeItem('branchPOSId');
    localStorage.removeItem('email');
    localStorage.removeItem('branchId');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('branchPOSSelling');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('closePrintObj');

    socketService.disconnect();

    navigate(`/${businessSlug}/login`, { replace: true });
  };

  useEffect(() => {
    if (rawPrintData) {
      try {
        const updateJson = JSON.parse(rawPrintData);
        setPrintData(updateJson);
      } catch (error) {
        console.error('Error parsing print data:', error);
      }
    }
  }, [rawPrintData]);

  const fetchSummaryData = useCallback(async () => {
    try {
      setIsLoading(true);
      const branchPOSSellingId = localStorage.getItem('branchPOSSelling');
      if (branchPOSSellingId) {
        const response = await dispatch(posHandler.getSummaryDataCancel(businessSlug, branchPOSSellingId));
        console.log('Summary data response:', response);
        if (response) {
          setSummaryData(response);
        }
      }
    } catch (error) {
      console.error('Error fetching summary data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [businessSlug, dispatch]);

  useEffect(() => {
    const fetchBusinessInfo = async () => {
      try {
        const branchId = localStorage.getItem('branchId');
        if (branchId) {
          const response = await dispatch(posHandler.getBranchInfo(businessSlug, branchId));
          if (response) {
            let businessName = '';
            let branchName = '';
            let vatCode = '';
            let taxNumber = '';
            let vatCalculator = '';

            if (response?.employee_info?.branch) {
              businessName = response.employee_info.branch.business?.company_name || '';
              branchName = response.employee_info.branch.branch_name || '';
              taxNumber = response.employee_info.branch.business?.tax_number || '';
              vatCalculator = response.employee_info.branch.business?.settingVatCalculation?.code || '';
            } else if (response?.branch_info?.business) {
              businessName = response.branch_info.business?.company_name || '';
              branchName = response.branch_info.branch_name || '';
              vatCode = response.branch_info.vat_code || '';
              taxNumber = response.branch_info.business.tax_number || '';
              vatCalculator = response.branch_info.business.settingVatCalculation?.code || '';
            }

            setBusinessInfo({
              businessName,
              branchName,
              vatCode,
              taxNumber,
              vatCalculator
            });
          }
        }
      } catch (error) {
        console.error('Error fetching business info:', error);
      }
    };
    const fetchPosData = async () => {
      try {
        setIsLoading(true);
        const branchId = localStorage.getItem('branchId');
        if (branchId) {
          const response = await dispatch(posHandler.getBranchPosById(businessSlug, branchId));
          if (response && response.length > 0) {
            setPosData(response[0]);
          }
        }
      } catch (error) {
        console.error('Error fetching POS data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPosData();
    fetchSummaryData();
    fetchBusinessInfo();
  }, [dispatch, businessSlug, fetchSummaryData]);

  const formatDate = dateString => {
    if (!dateString) return '-';

    try {
      return moment(dateString).format('DD/MM/YYYY HH:mm') + ' น.';
    } catch (error) {
      console.error('Error formatting date:', error);
      return '-';
    }
  };

  const formatCurrency = amount => {
    if (amount === null || amount === undefined) return '0.00';
    return new Intl.NumberFormat('th-TH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const getPosOrder = () => {
    if (posData && posData.order) {
      return posData.order;
    }
    return printData?.posNO || '-';
  };

  const getPosName = () => {
    if (posData && posData.name) {
      return posData.name;
    }
    return '-';
  };

  const getStartTime = () => {
    if (summaryData && summaryData.start_date_time) {
      return formatDate(summaryData.start_date_time);
    }

    if (
      posData &&
      posData.branchPosSelling &&
      posData.branchPosSelling.length > 0 &&
      posData.branchPosSelling[0].created_at
    ) {
      return formatDate(posData.branchPosSelling[0].created_at);
    }
    return printData?.startTime || '-';
  };

  const getEndTime = () => {
    if (summaryData && summaryData.end_date_time) {
      return formatDate(summaryData.end_date_time);
    }

    if (
      posData &&
      posData.branchPosSelling &&
      posData.branchPosSelling.length > 0 &&
      posData.branchPosSelling[0].end_time
    ) {
      return formatDate(posData.branchPosSelling[0].end_time);
    }
    return printData?.endTime || formatDate(new Date());
  };

  const getUserFullName = () => {
    return printData?.staff || '-';
  };

  if (isLoading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }

  return (
    <>
      {createPortal(
        <div className="section-to-print-receipt-page pb-10">
          <div className="bg-[#EFEFEF] pt-[4.00vw] pb-[12.79vw]">
            <div className="flex justify-center text-[0.78vw] w-full">
              <div
                id={'section-to-print'}
                className="w-[29.49vw] text-[0.98vw] font-[300] bg-[#FFF] px-[1.70vw] py-[1.31vw] flex flex-col gap-[0.59vw]"
              >
                <div className="text-center">
                  <div className="font-[700]">สรุปยอดขายตามรอบ</div>
                  <div className="font-[700]">
                    {businessInfo.businessName || ''} - ({businessInfo.branchName || ''})
                  </div>

                  <div>
                    {businessInfo.taxNumber ? `TAX ID : ${businessInfo.taxNumber || ''}` : ''}
                    {branch?.branch_info?.business?.settingVatCalculation?.code === 'INCLUDED'
                      ? '(Vat Included)'
                      : branch?.branch_info?.business?.settingVatCalculation?.code === 'SEPARATE'
                      ? '(Vat Excluded)'
                      : ''}
                  </div>
                </div>
                <div className="flex flex-col">
                  <div className="flex justify-between">
                    <div className="whitespace-nowrap">วันที่ {formatDate(new Date())}</div>
                    <div className="text-right">POS# : {getPosName()}</div>
                  </div>
                  <div className="flex justify-between">
                    <div className="whitespace-nowrap">POS NO : {getPosOrder()}</div>
                    <div className="text-right">พนักงาน : {getUserFullName()}</div>
                  </div>
                  <div className="flex justify-between">
                    <div className="">{posData && posData.tax_number ? `RD#${posData.tax_number}` : 'RD#-'}</div>
                  </div>

                  <div className="flex justify-between">
                    <div className="whitespace-nowrap">เลขที่ / เล่มที่</div>
                    <div className="text-right">
                      {`${summaryData.first_receipt_no || '-'} - ${summaryData.last_receipt_no || '-'}`}
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <div className="whitespace-nowrap">เวลาเริ่มขาย</div>
                    <div className="text-right">{getStartTime()}</div>
                  </div>
                  <div className="flex justify-between">
                    <div className="whitespace-nowrap">เวลาสิ้นสุด</div>
                    <div className="text-right">{getEndTime()}</div>
                  </div>
                  <div className="flex justify-between">
                    <div className="whitespace-nowrap">ระยะเวลาขาย</div>
                    <div className="text-right">{summaryData.operating_time || '00:00:00'}</div>
                  </div>

                  <div className="border-dashed border-t-[0.04vw] border-black h-0 mt-[1.28vw]"></div>
                  <div className="flex justify-between">
                    <div className="whitespace-nowrap">จำนวนบิลขาย</div>
                    <div className="text-right">{summaryData.total_bill_sale_count || 0}</div>
                  </div>
                  <div className="flex justify-between">
                    <div className="whitespace-nowrap">จำนวนบิลที่คืนสินค้า</div>
                    <div className="text-right">{summaryData.total_bill_return_count || 0}</div>
                  </div>
                  <div className="flex justify-between">
                    <div className="whitespace-nowrap">จำนวนบิลที่ยกเลิก</div>
                    <div className="text-right">{summaryData.total_bill_cancel_count || 0}</div>
                  </div>
                  <div className="flex justify-between">
                    <div className="whitespace-nowrap">จำนวนบิลที่ Void</div>
                    <div className="text-right">{summaryData.total_bill_void_count || 0}</div>
                  </div>
                  <div className="flex justify-between">
                    <div className="whitespace-nowrap">จำนวนบิล ออกใบกำกับภาษีเต็มรูปแบบ</div>
                    <div className="text-right">{summaryData.total_full_tax_invoice_count || 0}</div>
                  </div>
                  <div className="flex justify-between">
                    <div className="whitespace-nowrap">มูลค่า ออกใบกำกับภาษี เต็มรูปแบบ</div>
                    <div className="text-right">{formatCurrency(summaryData.total_full_tax_invoice_amount)}</div>
                  </div>

                  <div className="border-dashed border-t-[0.04vw] border-black h-0 mt-[1.28vw]"></div>
                  <div className="flex justify-between">
                    <div className="whitespace-nowrap">เงินสดตั้งต้น</div>
                    <div className="text-right">{formatCurrency(summaryData.initial_cash)}</div>
                  </div>
                  <div className="flex justify-between">
                    <div className="whitespace-nowrap">ยอดขาย</div>
                    <div className="text-right">{formatCurrency(summaryData.sales)}</div>
                  </div>
                  <div className="flex justify-between">
                    <div className="whitespace-nowrap">-Sale Refund</div>
                    <div className="text-right">{formatCurrency(summaryData.sale_refund)}</div>
                  </div>
                  <div className="flex justify-between">
                    <div className="whitespace-nowrap">-After Refund</div>
                    <div className="text-right">{formatCurrency(summaryData.after_refund)}</div>
                  </div>
                  <div className="flex justify-between">
                    <div className="whitespace-nowrap">-Item Discount</div>
                    <div className="text-right">{formatCurrency(summaryData.item_discount)}</div>
                  </div>
                  <div className="flex justify-between">
                    <div className="whitespace-nowrap">-Sub Discount</div>
                    <div className="text-right">{formatCurrency(summaryData.sub_discount)}</div>
                  </div>
                  <div className="flex justify-between">
                    <div className="whitespace-nowrap">ยอดรวมขาย</div>
                    <div className="text-right">{formatCurrency(summaryData.total_sales)}</div>
                  </div>
                  <div className="flex justify-between">
                    <div className="whitespace-nowrap">Service Charge</div>
                    <div className="text-right">{formatCurrency(summaryData.service_charge)}</div>
                  </div>

                  <div className="flex justify-between">
                    <div className="whitespace-nowrap font-[700]">ยอดสุทธิ</div>
                    <div className="text-right font-[700]">
                      {formatCurrency(Number(summaryData.total_final_amount) - Number(summaryData.sale_refund))}
                    </div>
                  </div>
                  <div className="border-dashed border-t-[0.04vw] border-black h-0 mt-[1.28vw]"></div>
                  <div className="flex justify-between">
                    <div className="whitespace-nowrap">ค่าธรรมเนียมบัตรเครดิต</div>
                    <div className="text-right">{formatCurrency(summaryData.credit_card_fee)}</div>
                  </div>
                  <div className="flex justify-between">
                    <div className="whitespace-nowrap">ภาษีมูลค่าเพิ่ม</div>
                    <div className="text-right">{formatCurrency(summaryData.vat_on_fee)}</div>
                  </div>
                  <div className="border-dashed border-t-[0.04vw] border-black h-0 mt-[1.28vw]"></div>
                  <div className="flex justify-between">
                    <div className="whitespace-nowrap">มูลค่าสินค้าไม่มีภาษี</div>
                    <div className="text-right">{formatCurrency(summaryData.non_taxable_amount)}</div>
                  </div>
                  <div className="flex justify-between">
                    <div className="whitespace-nowrap">มูลค่าสินค้ามีภาษี</div>
                    <div className="text-right">
                      {formatCurrency(summaryData.total_sale_amount + summaryData.credit_card_fee)}
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <div className="whitespace-nowrap">ภาษีมูลค่าเพิ่ม</div>
                    <div className="text-right">
                      {formatCurrency(summaryData.total_vat_amount + summaryData.vat_on_fee)}
                    </div>
                  </div>
                  <div className="flex flex-col justify-center px-16">
                    <div className="flex justify-between w-full font-[600]">
                      <div className="whitespace-nowrap">Net Amount</div>
                      <div className="text-right">{formatCurrency(summaryData.total_payment_amount || 0)}</div>
                    </div>
                    <div className="flex justify-between w-full font-[600]">
                      <div className="whitespace-nowrap">Net Amount Adj.</div>
                      <div className="text-right">
                        {formatCurrency((summaryData.total_payment_amount || 0) + (summaryData.loss_rounded || 0))}
                      </div>
                    </div>
                  </div>
                  <div className="border-dashed border-t-[0.04vw] border-black h-0 mt-[1.28vw]"></div>
                  <div className="flex justify-between">
                    <div className="whitespace-nowrap">ช่องทางการชำระ</div>
                  </div>
                  {Array.isArray(summaryData.system_balance) &&
                    summaryData.system_balance.map((paymentMethod, index) => (
                      <div key={index}>
                        <div className="flex justify-between">
                          <div className="whitespace-nowrap">{paymentMethod.payment_method_name}</div>
                          <div className="text-right">
                            {Array.isArray(paymentMethod.payment_providers) &&
                              paymentMethod.payment_providers.length === 0 &&
                              formatCurrency(
                                paymentMethod.payment_method_code === 'CASH'
                                  ? Number(paymentMethod.amount) - Number(summaryData.sale_refund)
                                  : paymentMethod.amount
                              )}
                          </div>
                        </div>

                        {Array.isArray(paymentMethod.payment_providers) &&
                          paymentMethod.payment_providers.map((provider, providerIndex) => (
                            <div className="flex justify-between" key={`provider-${providerIndex}`}>
                              <div className="whitespace-nowrap pl-[1.5vw]">- {provider.payment_provider_name}</div>
                              <div className="text-right">
                                {formatCurrency(
                                  provider.payment_method_code === 'CASH'
                                    ? Number(provider.amount) - Number(summaryData.sale_refund)
                                    : provider.amount
                                )}
                              </div>
                            </div>
                          ))}
                      </div>
                    ))}

                  <div className="border-dashed border-t-[0.04vw] border-black h-0 mt-[1.28vw]"></div>
                  <div className="flex justify-between">
                    <div className="whitespace-nowrap">กำไร(ขาดทุน)ปัดเศษ</div>
                    <div className="text-right">{formatCurrency(summaryData.loss_rounded || 0)}</div>
                  </div>
                  <div className="flex justify-between">
                    <div className="whitespace-nowrap">ยอดนับเงินสด</div>
                    <div className="text-right">{formatCurrency(summaryData.cash_total || 0)}</div>
                  </div>
                  <div className="flex justify-between font-[600] px-16">
                    <div className="whitespace-nowrap">Diff</div>
                    <div className="text-right">{formatCurrency(summaryData.summary?.diff || 0)}</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-center w-full h-auto print-hidden fixed bottom-0 gap-[min(2.56vw,26.18px)] bg-[#EFEFEF] py-[min(3.42vw,35px)]">
              <CustomBtn
                onClick={handleEndSession}
                className={`leading-[0.8] w-[min(16.89vw,173px)] text-white font-[400] text-16 min-h-[min(3.91vw,40px)] rounded-[min(0.78vw,8px)] btn-error-active`}
                btntxt={'สิ้นสุด'}
              />
              <CustomBtn
                onClick={() => window.print()}
                className={`leading-[0.8] w-[min(16.89vw,173px)] text-white font-[400] text-16 min-h-[min(3.91vw,40px)] rounded-[min(0.78vw,8px)] btn-main-active capitalize`}
                btntxt={'Print'}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
